import { isAxiosError } from 'axios';
import { getUtilitiesFileSignedUrl, type UtilitiesFileSource } from '~/api/projects2';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';

/** Minimum file shape needed to download and describe a Utilities-tab attachment. */
export type UtilitiesDownloadableFile = {
  id: string;
  name: string;
  type?: string;
  extension?: string;
};

export function utilitiesFileDownloadProperties(file: UtilitiesDownloadableFile, projectId: string, filesize: number) {
  return {
    project_id: projectId,
    file_id: file.id,
    file_name: file.name,
    file_type: file.extension || file.type || 'unknown',
    file_size: filesize,
  };
}

const reasonForError = (e: unknown) => (isAxiosError(e) && e.response ? String(e.response.status) : 'network_error');

const reportDownloadFailure = (file: UtilitiesDownloadableFile, reason: string) => {
  captureTypedPosthogEvent(POSTHOG_EVENTS.fileDownloadFailed, { source: 'utilities', file_name: file.name, reason });
  notifyDownloadError();
};

/**
 * Hands a freshly signed attachment URL to the browser's download manager, which streams the file
 * to disk, then captures `utilities_file_downloaded`. Signing failures toast and capture
 * `file_download_failed`; a transfer that fails after hand-off shows only in the browser.
 */
export async function downloadAndTrackUtilitiesFile(
  file: UtilitiesDownloadableFile,
  source: UtilitiesFileSource,
  projectId: string | undefined,
): Promise<void> {
  if (!projectId) {
    reportDownloadFailure(file, 'missing_project');
    return;
  }

  const signed = await getUtilitiesFileSignedUrl(projectId, source, file.id).catch((e: unknown) => {
    reportDownloadFailure(file, reasonForError(e));
    return null;
  });
  if (!signed) return;

  // Signed with `Content-Disposition: attachment`, so the navigation downloads without leaving the page.
  window.location.href = signed.url;

  captureTypedPosthogEvent(
    POSTHOG_EVENTS.utilitiesFileDownloaded,
    utilitiesFileDownloadProperties(file, projectId, signed.filesize),
  );
}
